"use client";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import styles from "../../styles/AsignacionProgramasStyles/PantallaDeAsignacionProgramaMateria.module.css";
import { getAllSubjects } from "../../api/subjects";
import { getPrograms } from "../../api/programs";
import { getMateriasPorPrograma } from "../../api/planestudio";

function SubjectSearch() {
  // Estado dinámico de materias provenientes del backend/local cache
  const [allSubjects, setAllSubjects] = useState([]); // Todas las materias
  const [subjects, setSubjects] = useState([]); // Materias filtradas por programa
  const [query, setQuery] = useState("");
  
  // Estado para filtro por programa
  const [programs, setPrograms] = useState([]);
  const [selectedProgramFilter, setSelectedProgramFilter] = useState("all"); // "all" = todas las materias
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Estado para filtro por sección académica (viene del ProgramSelector)
  const [selectedSeccionId, setSelectedSeccionId] = useState(null);
  
  // Estado para paginación visual (lazy rendering)
  const ITEMS_PER_PAGE = 30;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Escuchar evento seccion:changed del ProgramSelector
  useEffect(() => {
    const handleSeccionChanged = (e) => {
      const detail = e.detail || {};
      const newSeccionId = detail.id_seccion || null;
      console.log('[SubjectSearch] Sección académica cambiada a:', newSeccionId);
      setSelectedSeccionId(newSeccionId);
      setVisibleCount(ITEMS_PER_PAGE);
    };
    window.addEventListener('seccion:changed', handleSeccionChanged);
    return () => window.removeEventListener('seccion:changed', handleSeccionChanged);
  }, []);

  // Cargar programas para el dropdown de filtro
  useEffect(() => {
    let mounted = true;
    const loadPrograms = async () => {
      try {
        console.log('[SubjectSearch] Cargando programas para filtro...');
        const data = await getPrograms();
        if (mounted) {
          setPrograms(Array.isArray(data) ? data : []);
          console.log('[SubjectSearch] Programas cargados:', data.length);
        }
      } catch (e) {
        console.error('[SubjectSearch] Error al cargar programas:', e);
      }
    };
    loadPrograms();
    return () => { mounted = false; };
  }, []);

  // Cargar todas las materias inicialmente usando getAllSubjects (sin paginación)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoadingSubjects(true);
        console.log('[SubjectSearch] Cargando TODAS las materias desde API (sin paginación)...');
        // Usar getAllSubjects para obtener todas las materias sin límite
        const materias = await getAllSubjects();
        if (mounted) {
          // getAllSubjects ya devuelve un array plano
          const materiasArray = Array.isArray(materias) ? materias : [];
          setAllSubjects(materiasArray);
          setSubjects(materiasArray);
          console.log('[SubjectSearch] ✅ Total materias cargadas:', materiasArray.length);
        }
      } catch (e) {
        console.error('[SubjectSearch] Error al cargar materias:', e);
      } finally {
        if (mounted) setLoadingSubjects(false);
      }
    };
    load();
    
    const onUpdate = async () => {
      try {
        console.log('[SubjectSearch] Evento subjects:updated recibido, recargando...');
        const materias = await getAllSubjects();
        const materiasArray = Array.isArray(materias) ? materias : [];
        setAllSubjects(materiasArray);
        // Si hay filtro de programa activo, recargar según el filtro
        if (selectedProgramFilter !== "all") {
          const materiasPrograma = await getMateriasPorPrograma(selectedProgramFilter);
          setSubjects(materiasPrograma);
        } else {
          setSubjects(materiasArray);
        }
        console.log('[SubjectSearch] Materias actualizadas:', materiasArray.length, 'materias');
      } catch {}
    };
    window.addEventListener('subjects:updated', onUpdate);
    return () => { mounted = false; window.removeEventListener('subjects:updated', onUpdate); };
  }, [selectedProgramFilter]);

  // Manejar cambio de filtro por programa
  const handleProgramFilterChange = async (e) => {
    const value = e.target.value;
    console.log('[SubjectSearch] 📋 Filtro de programa cambiado a:', value);
    setSelectedProgramFilter(value);
    setLoadingSubjects(true);
    setVisibleCount(ITEMS_PER_PAGE); // Reset paginación visual
    
    try {
      if (value === "all") {
        // Mostrar todas las materias desde esc_materias
        console.log('[SubjectSearch] Mostrando TODAS las materias');
        setSubjects(allSubjects);
      } else {
        // Obtener materias del programa desde esc_planes_estudio
        console.log('[SubjectSearch] Obteniendo materias del programa ID:', value);
        const materiasPrograma = await getMateriasPorPrograma(value);
        console.log('[SubjectSearch] Materias del programa:', materiasPrograma.length);
        setSubjects(materiasPrograma);
      }
    } catch (error) {
      console.error('[SubjectSearch] Error al filtrar por programa:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Normalización simple para coincidencias sin acentos y case-insensitive
  const normalize = (str) => str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const normalizedQuery = normalize(query.trim());

  // Función para normalizar créditos a número (manejando null, undefined, strings)
  const normalizeCredits = (creditos) => {
    if (creditos === null || creditos === undefined || creditos === '' || creditos === '—') {
      return null;
    }
    const parsed = parseInt(creditos, 10);
    return isNaN(parsed) ? null : parsed;
  };

  // Map subjects to current UI shape (keep original design). Backend mapping uses nombre & creditos
  const uiSubjects = subjects.map(s => ({
    id: s.id_materia || s.id || s.pk || s.uuid || null,
    name: s.nombre || s.nombre_materia || s.name || '',
    credits: normalizeCredits(s.creditos ?? s.credits),
    academia: s.academia || s.academia_nombre || '',
    id_seccion: s.id_seccion || null,
  }));

  // LOG: Materias mapeadas para UI
  console.log('[SubjectSearch] Total materias disponibles:', uiSubjects.length);

  // Filtrar por sección académica (client-side, cada materia tiene id_seccion)
  const seccionFiltered = selectedSeccionId
    ? uiSubjects.filter(s => String(s.id_seccion) === String(selectedSeccionId))
    : uiSubjects;

  // Filtrar por query de texto
  const filteredSubjects = normalizedQuery
    ? seccionFiltered.filter(s => normalize(s.name).includes(normalizedQuery))
    : seccionFiltered;

  // Limitar materias visibles para mejor rendimiento
  const visibleSubjects = filteredSubjects.slice(0, visibleCount);
  const hasMore = filteredSubjects.length > visibleCount;
  
  // Resetear contador cuando cambia el filtro o búsqueda
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    // Detectar si se está limpiando el filtro
    if (query && !newValue) {
      console.log('[SubjectSearch] 🧹 Filtro limpiado/reiniciado');
    }
    setQuery(newValue);
    setVisibleCount(ITEMS_PER_PAGE); // Reset al cambiar búsqueda
  };
  
  // Cargar más materias
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  const handleSelectSubject = (subject) => {
    try {
      console.log('[SubjectSearch] Materia seleccionada:', subject.name);
      window.dispatchEvent(new CustomEvent('subject:selected', { detail: subject }));
    } catch {}
  };

  const handleDragStart = (e, subject) => {
    try {
      console.log('[SubjectSearch] Iniciando drag de materia:', subject.name);
      e.dataTransfer.setData('application/json', JSON.stringify({ ...subject }));
    } catch {}
  };

  return (
    <aside 
      className={styles.subjectSearchPanel}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        height: '100%',
        maxHeight: 'calc(100vh - 180px)',
        overflow: 'hidden',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}
    >
      {/* Encabezado */}
      <div style={{ 
        marginBottom: '16px', 
        flexShrink: 0,
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '12px'
      }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#002f6c'
        }}>
          📚 Catálogo de Materias
        </h3>
        <p style={{ 
          margin: '4px 0 0 0', 
          fontSize: '12px', 
          color: '#6b7280'
        }}>
          Arrastra las materias hacia los períodos
        </p>
      </div>

      {/* Dropdown de filtro por programa */}
      <div style={{ marginBottom: '12px', flexShrink: 0 }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '6px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Filtrar por Programa
        </label>
        <select
          className={styles.selectField}
          value={selectedProgramFilter}
          onChange={handleProgramFilterChange}
          disabled={loadingSubjects}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            fontSize: '13px',
            backgroundColor: '#fff',
            cursor: 'pointer'
          }}
        >
          <option value="all">📋 Todas las materias</option>
          {programs.map(p => (
            <option key={p.id} value={p.id}>
              {p.name || p.nombre_programa || `Programa ${p.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* Campo de búsqueda */}
      <div style={{ position: 'relative', marginBottom: '12px', flexShrink: 0 }}>
        <svg 
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '18px',
            height: '18px',
            color: '#9ca3af',
            pointerEvents: 'none'
          }}
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor"
        >
          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          placeholder="Buscar materia por nombre..."
          aria-label="Buscar Materia"
          value={query}
          onChange={handleSearchChange}
          style={{
            width: '100%',
            padding: '10px 12px 10px 38px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            fontSize: '13px',
            backgroundColor: '#fff',
            boxSizing: 'border-box'
          }}
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '2px'
            }}
            title="Limpiar búsqueda"
          >
            ✕
          </button>
        )}
      </div>
      
      {/* Contador de materias */}
      <div style={{ 
        marginBottom: '12px', 
        flexShrink: 0, 
        padding: '8px 12px', 
        backgroundColor: '#f3f4f6', 
        borderRadius: '6px',
        fontSize: '12px',
        color: '#374151'
      }}>
        {hasMore ? (
          <>
            Mostrando <span style={{ fontWeight: '600' }}>{visibleCount}</span> de <span style={{ fontWeight: '600' }}>{filteredSubjects.length}</span> materias
          </>
        ) : (
          <>
            <span style={{ fontWeight: '600' }}>{filteredSubjects.length}</span> materias
          </>
        )}
        {selectedProgramFilter !== "all" && " del programa"}
        {selectedSeccionId && " de la sección"}
        {query && ` para "${query}"`}
      </div>
      
      {/* Indicador de carga */}
      {loadingSubjects && (
        <p style={{ fontSize: '12px', color: '#6b7280', padding: '16px 0', textAlign: 'center', flexShrink: 0 }}>
          Cargando materias...
        </p>
      )}
      
      {/* Mensaje cuando no hay materias */}
      {!loadingSubjects && filteredSubjects.length === 0 && (
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '24px 8px',
          color: '#9ca3af'
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginBottom: '12px', opacity: 0.5 }}>
            <path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <p style={{ fontSize: '13px', margin: 0, textAlign: 'center' }}>
            {selectedProgramFilter !== "all" 
              ? "No hay materias asignadas a este programa" 
              : selectedSeccionId
                ? "No hay materias en esta sección académica"
                : query 
                  ? `No se encontraron materias para "${query}"` 
                  : "No se encontraron materias"}
          </p>
        </div>
      )}
      
      {/* Lista de materias con scroll */}
      {!loadingSubjects && filteredSubjects.length > 0 && (
        <div 
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '4px',
            marginTop: '4px'
          }}
        >
          {visibleSubjects.map((subject, index) => (
            <div
              key={(subject.id || subject.name) + '-' + index}
              role="button"
              tabIndex={0}
              draggable
              onDragStart={(e) => handleDragStart(e, subject)}
              onClick={() => handleSelectSubject(subject)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                marginBottom: '6px',
                borderRadius: '6px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                cursor: 'grab',
                transition: 'all 0.15s ease',
                width: '100%',
                boxSizing: 'border-box'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.borderColor = '#002f6c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              {/* Indicador de drag */}
              <div style={{ 
                color: '#9ca3af', 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm8-12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
                </svg>
              </div>
              
              {/* Información de la materia */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  color: '#1f2937',
                  wordBreak: 'break-word',
                  lineHeight: '1.4'
                }}>
                  {subject.name}
                </p>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginTop: '3px'
                }}>
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#6b7280',
                    backgroundColor: '#e5e7eb',
                    padding: '2px 6px',
                    borderRadius: '4px'
                  }}>
                    {subject.credits !== null && subject.credits !== undefined ? `${subject.credits} créditos` : 'Sin créditos'}
                  </span>
                  {subject.academia && (
                    <span style={{ 
                      fontSize: '11px', 
                      color: '#6b7280'
                    }}>
                      {subject.academia}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Botón Mostrar más */}
          {hasMore && (
            <button
              onClick={handleLoadMore}
              style={{
                width: '100%',
                padding: '12px',
                marginTop: '8px',
                backgroundColor: '#002f6c',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#001f4c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#002f6c'}
            >
              Mostrar más ({filteredSubjects.length - visibleCount} restantes)
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

export default SubjectSearch;
